import { useEffect } from 'react';
import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { Avatar } from './Avatar';
import { FestivalTouch } from './brand/FestivalTouch';
import {
  AtSign,
  Lightbulb,
  LogOut,
  Mail,
  Moon,
  Phone,
  Search,
  Settings,
  Star,
  Sun,
  User,
  Users,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import {
  familiesAtom,
  familiesLoadedAtom,
  fullScreenPageAtom,
  headerActionsAtom,
  homeSearchOpenAtom,
  openConversationIdAtom,
  currentFamilyIdAtom,
} from '../store';
import {
  useAlerts,
  useAuth,
  useE2EE,
  useInvites,
  useFamilyMemberLeft,
  usePictureKeys,
  useFamilyDeleted,
  useFamilyUpdated,
  useJoinRequestCounts,
  useSafetySync,
  useMyJoinDecisions,
  useOutboxSync,
  useSearchIndexSync,
  usePictureUpkeep,
  useMemberLabelsSync,
  useViewingPresence,
} from '../hooks';
import { FamilySwitcher } from './family/FamilySwitcher';
import { WaitingForFamily } from './family/WaitingForFamily';
import { WebSocketProvider, useWebSocket } from '../hooks/useWebSocket';
import { WS_EVENTS } from '@twinden/shared';
import { useScheduledReseal } from '../hooks/useScheduled';
import { CallProvider, useCall } from '../hooks/useCall';
import { CallOverlay } from './call/CallOverlay';
import { GatheringRing } from './chat/GatheringRing';
import { PasskeyHereOffer } from './PasskeyHereOffer';
import { UpdateRequiredBanner } from './UpdateRequiredBanner';
import { AppNav } from './AppNav';
import { LogoMark } from './brand/Logo';
import { useT, type MessageKey } from '../i18n';

interface LayoutProps {
  children: React.ReactNode;
}

// Family apps put their own name under a small family switcher (Living
// Room); every other page keeps the family's name as the title.
const PAGE_TITLES: Array<[string, MessageKey]> = [
  ['/documents', 'account.nav.documents'],
  ['/links', 'account.nav.links'],
  ['/lists', 'account.nav.lists'],
];

// With no family yet, only these pages open; the rest show the waiting
// screen (D153): Settings, plus the account pages and old-style invites.
// Shape Twinden is for everyone signed in (M55, D198).
const OPEN_WITHOUT_FAMILY = ['/settings', '/profile', '/invites', '/ideas'];

// Room under the floating nav on phones, so the last row isn't hidden by it.
const FLOATING_NAV_SPACE =
  'calc(76px + max(env(safe-area-inset-bottom), 16px))';

function LayoutInner({ children }: LayoutProps) {
  const { colorMode, toggleColorMode } = useColorMode();
  const { currentUser, logout, setCurrentUser } = useAuth();
  const { count: inviteCount, refresh: refreshInvites } = useInvites();
  // An invitation arrived (D351): the badge shows it at once.
  const { onEvent } = useWebSocket();
  useEffect(
    () =>
      onEvent(({ event }) => {
        if (event === WS_EVENTS.FAMILY_INVITE_RECEIVED) void refreshInvites();
      }),
    [onEvent, refreshInvites]
  );
  // People asking to join families I approve for (D154).
  const { total: requestCount } = useJoinRequestCounts();
  // Someone left or deleted their account: members, roles, lists (M51).
  useFamilyMemberLeft();
  // Every family's picture keys, so faces open (M72, D292).
  usePictureKeys();
  // A root deleted a family: it goes from this device (M54, D189).
  useFamilyDeleted();
  // A root renamed the family or changed its handle (M59, D216).
  useFamilyUpdated();
  // Blocked people and report notices (M58, D207, D208).
  useSafetySync();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const fullScreenPage = useAtomValue(fullScreenPageAtom);
  const currentFamilyId = useAtomValue(currentFamilyIdAtom);
  // An open chat or a checklist takes the whole screen on phones.
  const chatFocusMode =
    useAtomValue(openConversationIdAtom) !== null || fullScreenPage;
  const pageTitle =
    PAGE_TITLES.find(([prefix]) => location.pathname.startsWith(prefix))?.[1] ??
    null;
  const [searchOpen, setSearchOpen] = useAtom(homeSearchOpenAtom);
  const headerActions = useAtomValue(headerActionsAtom);
  // No family yet (D153, D156). Until the list first loads, trust the
  // account's own flag so a new joiner doesn't see an empty chat list.
  const families = useAtomValue(familiesAtom);
  const familiesLoaded = useAtomValue(familiesLoadedAtom);
  const noFamily = familiesLoaded
    ? families.length === 0
    : !!currentUser?.awaitingFirstFamily;
  const showWaiting =
    noFamily &&
    !OPEN_WITHOUT_FAMILY.some((prefix) => location.pathname.startsWith(prefix));
  const onHome = location.pathname === '/' && !noFamily;
  // Let in: the flag no longer applies, even before the next load.
  useEffect(() => {
    if (
      familiesLoaded &&
      families.length > 0 &&
      currentUser?.awaitingFirstFamily
    )
      setCurrentUser({ ...currentUser, awaitingFirstFamily: false });
  }, [familiesLoaded, families.length, currentUser, setCurrentUser]);
  // Message sounds, ringtones and vibration while the app is open (#3).
  useAlerts();
  // Unsent messages go out when the connection is back (M23).
  // End-to-end encryption keys for this device (M28).
  useE2EE();
  useOutboxSync();
  // Search across chats runs on this device (M25).
  useSearchIndexSync();
  // Readable pictures sealed, and my picture's key kept for every family
  // (M72, D295, D297).
  usePictureUpkeep();
  // Scheduled messages left needing a new seal after a rotation (D239).
  useScheduledReseal();
  useMemberLabelsSync();
  // What's on screen, so the server doesn't notify about it.
  useViewingPresence();
  // A family let me in: open it (D155).
  useMyJoinDecisions();

  // The badge is how an invitee finds out about a new family invite, so
  // refresh it on every navigation instead of only on a full page load.
  useEffect(() => {
    refreshInvites();
  }, [location.pathname, refreshInvites]);

  // Search belongs to the home it was opened on.
  useEffect(() => {
    if (!onHome) setSearchOpen(false);
  }, [onHome, setSearchOpen]);

  // No text selection on touch screens while the app is open (theme global).
  useEffect(() => {
    document.documentElement.classList.add('in-app');
    return () => document.documentElement.classList.remove('in-app');
  }, []);

  return (
    <Flex
      direction="column"
      h="var(--app-height, 100vh)"
      overflow="hidden"
      // Fixed to what the user can see, which iOS scrolls down when the
      // keyboard opens (lib/viewport.ts). Also the containing block for
      // absolutely positioned popovers (menus), so overflow clips them.
      position="fixed"
      top="var(--app-top, 0px)"
      left={0}
      right={0}
      bg="canvas"
    >
      {/* Header: the family's name is the title (Living Room). An open chat
          takes the whole screen on phones; its own header has the back
          button. */}
      <Flex
        as="header"
        align="center"
        gap={1}
        px={{ base: 2, md: 4 }}
        pt="max(env(safe-area-inset-top), 0.5rem)"
        pb={1}
        minH="60px"
        flexShrink={0}
        display={{ base: chatFocusMode ? 'none' : 'flex', md: 'flex' }}
      >
        <HStack spacing={2} flex="1" minW={0}>
          <LogoMark
            size={30}
            display={{
              base: noFamily ? 'inline-flex' : 'none',
              md: 'inline-flex',
            }}
          />
          {pageTitle ? (
            <Box minW={0} pl={{ base: 2, md: 0 }}>
              <FamilySwitcher variant="compact" />
              <Heading
                as="h1"
                fontSize={{ base: '30px', md: '26px' }}
                lineHeight={1.05}
                noOfLines={1}
              >
                {t(pageTitle)}
              </Heading>
            </Box>
          ) : (
            <Box minW={0}>
              <FamilySwitcher />
            </Box>
          )}
          {/* Festival mode (D310): a small touch on a festival's own days. */}
          {onHome && !pageTitle && <FestivalTouch />}
        </HStack>

        {/* Desktop: the family apps sit in the header. */}
        <Box display={{ base: 'none', md: noFamily ? 'none' : 'block' }}>
          <AppNav placement="inline" />
        </Box>

        <HStack spacing={0} flex={{ md: 1 }} justify="flex-end">
          {onHome && (
            <IconButton
              aria-label={
                searchOpen
                  ? t('account.header.closeSearch')
                  : t('account.header.searchChats')
              }
              aria-pressed={searchOpen}
              icon={<Search size={22} />}
              variant="ghost"
              colorScheme="gray"
              size="lg"
              onClick={() => setSearchOpen((open) => !open)}
            />
          )}
          {headerActions}
          {/* Call history (Living Room "Calls"). */}
          {onHome && (
            <IconButton
              aria-label={t('account.header.calls')}
              icon={<Phone size={22} />}
              variant="ghost"
              colorScheme="gray"
              size="lg"
              onClick={() => navigate('/calls')}
            />
          )}
          {/* Family-app pages use the room for their own buttons. */}
          {!pageTitle && !noFamily && (
            <IconButton
              aria-label={t('account.header.starred')}
              icon={<Star size={22} />}
              variant="ghost"
              colorScheme="gray"
              size="lg"
              onClick={() => navigate('/starred')}
            />
          )}
          <Menu placement="bottom-end">
            <MenuButton
              aria-label={t('account.header.yourAccount')}
              borderRadius="full"
              ml={1}
              position="relative"
            >
              <Avatar
                size="sm"
                name={currentUser?.displayName}
                src={currentUser?.avatarUrl ?? undefined}
                ownerId={currentUser?.id}
                familyId={currentFamilyId}
              />
              {inviteCount + requestCount > 0 && (
                <Box
                  position="absolute"
                  top="-2px"
                  right="-2px"
                  w="12px"
                  h="12px"
                  borderRadius="full"
                  bg="saffron"
                  border="2px solid"
                  borderColor="canvas"
                  aria-hidden="true"
                />
              )}
            </MenuButton>
            <MenuList>
              <Box px={3} py={2}>
                <Text fontWeight="semibold">{currentUser?.displayName}</Text>
                <Text fontSize="sm" color="muted">
                  @{currentUser?.handle}
                </Text>
              </Box>
              <MenuDivider />
              <MenuItem
                icon={<Users size={16} />}
                onClick={() => navigate('/family')}
              >
                <HStack justify="space-between" w="full">
                  <Text>{t('account.menu.families')}</Text>
                  {requestCount > 0 && (
                    <Badge
                      bg="saffron"
                      color="#1F1D1B"
                      aria-label={t('account.requests.asking', {
                        count: requestCount,
                      })}
                    >
                      {requestCount}
                    </Badge>
                  )}
                </HStack>
              </MenuItem>
              <MenuItem
                icon={<Mail size={16} />}
                onClick={() => navigate('/invites')}
              >
                <HStack justify="space-between" w="full">
                  <Text>{t('account.menu.invitations')}</Text>
                  {inviteCount > 0 && (
                    <Badge bg="saffron" color="#1F1D1B">
                      {inviteCount}
                    </Badge>
                  )}
                </HStack>
              </MenuItem>
              <MenuItem
                icon={<AtSign size={16} />}
                onClick={() => navigate('/mentions')}
              >
                {t('account.menu.mentions')}
              </MenuItem>
              <MenuItem
                icon={<User size={16} />}
                onClick={() => navigate('/profile')}
              >
                {t('account.menu.profile')}
              </MenuItem>
              <MenuItem
                icon={<Settings size={16} />}
                onClick={() => navigate('/settings')}
              >
                {t('account.menu.settings')}
              </MenuItem>
              <MenuItem
                icon={<Lightbulb size={16} />}
                onClick={() => navigate('/ideas')}
              >
                {t('account.menu.ideas')}
              </MenuItem>
              <MenuItem
                icon={
                  colorMode === 'light' ? <Moon size={16} /> : <Sun size={16} />
                }
                onClick={toggleColorMode}
              >
                {colorMode === 'light'
                  ? t('account.menu.darkMode')
                  : t('account.menu.lightMode')}
              </MenuItem>
              <MenuDivider />
              <MenuItem
                icon={<LogOut size={16} />}
                onClick={logout}
                color="danger"
              >
                {t('account.menu.logOut')}
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* The server asked for an update (426, #82): stays, never blocks. */}
      <UpdateRequiredBanner />

      {/* Main content */}
      <Box
        flex="1"
        minH={0}
        overflow="hidden"
        pb={{
          base: chatFocusMode || noFamily ? 0 : FLOATING_NAV_SPACE,
          md: 0,
        }}
      >
        {showWaiting ? <WaitingForFamily /> : children}
      </Box>

      {/* Phones: the family apps float at the bottom. */}
      <Box
        display={{
          base: chatFocusMode || noFamily ? 'none' : 'block',
          md: 'none',
        }}
      >
        <AppNav placement="floating" />
      </Box>

      {/* In-call UI (M19) sits above everything while a call is live, or
          floats as a small tile when minimised (M70, D267). */}
      <CallOverlay />
      {/* "Come now" for a gathering (M71, D286), held during a call. */}
      <GatheringRingBesideCalls />
      {/* Signed in with a phone's passkey: offer one here (M41). */}
      <PasskeyHereOffer />
    </Flex>
  );
}

// Its own component, so a call's frequent state changes re-render only this.
function GatheringRingBesideCalls() {
  const { state } = useCall();
  return <GatheringRing held={state.status !== 'idle'} />;
}

export function Layout({ children }: LayoutProps) {
  return (
    <WebSocketProvider>
      <CallProvider>
        <LayoutInner>{children}</LayoutInner>
      </CallProvider>
    </WebSocketProvider>
  );
}
